import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const REQUIRED_METADATA_OPTIONS = ["title","document_number","revision","department","site","author","reviewer","approver","effective_date","related_documents"];

export default function Standards() {
  const { activeWorkspace } = useAuth();
  const [std, setStd] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeWorkspace) return;
    api.get(`/workspaces/${activeWorkspace.id}/standards`).then(({data}) => setStd(data || { terminology: [], abbreviations: [], required_metadata: [] }));
  }, [activeWorkspace]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/workspaces/${activeWorkspace.id}/standards`, {
        language_variant: std.language_variant || "Australian English",
        writing_style: std.writing_style || "",
        custom_guidance: std.custom_guidance || "",
        terminology: std.terminology || [],
        abbreviations: std.abbreviations || [],
        formatting_rules: std.formatting_rules || "",
        required_metadata: std.required_metadata || [],
        revision_format: std.revision_format || "",
        naming_pattern: std.naming_pattern || "",
      });
      toast.success("Standards saved");
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  if (!std) return null;

  const addTerm = () => setStd({...std, terminology: [...(std.terminology||[]), {preferred:"", avoid:"", reason:""}]});
  const removeTerm = (i) => setStd({...std, terminology: (std.terminology||[]).filter((_,idx)=>idx!==i)});
  const updateTerm = (i, k, v) => setStd({...std, terminology: (std.terminology||[]).map((t,idx)=>idx===i?{...t,[k]:v}:t)});

  const addAbbr = () => setStd({...std, abbreviations: [...(std.abbreviations||[]), {short:"", long:""}]});
  const removeAbbr = (i) => setStd({...std, abbreviations: (std.abbreviations||[]).filter((_,idx)=>idx!==i)});
  const updateAbbr = (i, k, v) => setStd({...std, abbreviations: (std.abbreviations||[]).map((t,idx)=>idx===i?{...t,[k]:v}:t)});

  const toggleReq = (key) => {
    const cur = new Set(std.required_metadata || []);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    setStd({...std, required_metadata: [...cur]});
  };

  return (
    <div className="max-w-5xl mx-auto p-8 space-y-6" data-testid="standards-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-display text-2xl font-bold text-white">Workspace standards</h1><p className="text-sm text-slate-400">Terminology, style, metadata requirements and rules that apply to every document.</p></div>
        <button onClick={save} disabled={busy} className="fx-btn-primary text-white text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2" data-testid="save-standards"><Save size={14}/> {busy?"Saving…":"Save"}</button>
      </div>

      <Section title="Language">
        <label className="block">
          <div className="text-xs text-slate-400 mb-1">Default language variant</div>
          <select value={std.language_variant} onChange={(e)=>setStd({...std, language_variant:e.target.value})} className="w-full max-w-xs bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100" data-testid="std-language">
            <option>Australian English</option><option>British English</option><option>American English</option>
          </select>
        </label>
      </Section>

      <Section title="Writing style">
        <input value={std.writing_style || ""} onChange={(e)=>setStd({...std, writing_style:e.target.value})} placeholder="Formal, Technical, Concise" className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100" data-testid="std-style"/>
      </Section>

      <Section title="Required metadata">
        <p className="text-xs text-slate-500 mb-2">Metadata that Fexidoc will highlight as required across every document.</p>
        <div className="flex flex-wrap gap-2">
          {REQUIRED_METADATA_OPTIONS.map((k) => {
            const on = (std.required_metadata || []).includes(k);
            return (
              <button key={k} onClick={()=>toggleReq(k)} className={`px-3 py-1.5 rounded-full border text-xs transition ${on ? "bg-cyan-500/15 border-cyan-400/50 text-cyan-200" : "bg-[#0D1224] border-[#1E293B] text-slate-400 hover:text-white"}`} data-testid={`req-meta-${k}`}>
                {k.replace(/_/g,' ')}
              </button>
            );
          })}
        </div>
      </Section>

      <Section title="Document naming">
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Revision format</div>
            <input value={std.revision_format || ""} onChange={(e)=>setStd({...std, revision_format:e.target.value})} placeholder="e.g. Rev {N} or A, B, C" className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 mono" data-testid="std-revision-format"/>
          </label>
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Filename pattern</div>
            <input value={std.naming_pattern || ""} onChange={(e)=>setStd({...std, naming_pattern:e.target.value})} placeholder="e.g. {prefix}-{number}_{title}" className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 mono" data-testid="std-naming-pattern"/>
          </label>
        </div>
      </Section>

      <Section title="Terminology">
        <div className="border border-[#1E293B] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0F1F] text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-3 py-2">Preferred</th><th className="text-left px-3 py-2">Avoid</th><th className="text-left px-3 py-2">Reason</th><th className="w-10"></th></tr>
            </thead>
            <tbody>
              {(std.terminology || []).map((t, i) => (
                <tr key={i} className="border-t border-[#1E293B]">
                  <td className="px-2 py-1"><input value={t.preferred} onChange={(e)=>updateTerm(i,'preferred',e.target.value)} className="w-full bg-transparent px-2 py-1 text-sm text-slate-100 outline-none" data-testid={`term-preferred-${i}`}/></td>
                  <td className="px-2 py-1"><input value={t.avoid} onChange={(e)=>updateTerm(i,'avoid',e.target.value)} className="w-full bg-transparent px-2 py-1 text-sm text-slate-100 outline-none" data-testid={`term-avoid-${i}`}/></td>
                  <td className="px-2 py-1"><input value={t.reason} onChange={(e)=>updateTerm(i,'reason',e.target.value)} className="w-full bg-transparent px-2 py-1 text-sm text-slate-100 outline-none" data-testid={`term-reason-${i}`}/></td>
                  <td className="px-2"><button onClick={()=>removeTerm(i)} className="text-slate-500 hover:text-red-400" data-testid={`term-remove-${i}`}><Trash2 size={14}/></button></td>
                </tr>
              ))}
              {(std.terminology||[]).length === 0 && <tr><td colSpan={4} className="text-center py-6 text-sm text-slate-500">No terminology rules yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <button onClick={addTerm} className="mt-3 text-sm text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1" data-testid="add-term"><Plus size={14}/> Add terminology rule</button>
      </Section>

      <Section title="Abbreviations">
        <div className="border border-[#1E293B] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0F1F] text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-3 py-2 w-32">Short</th><th className="text-left px-3 py-2">Long form</th><th className="w-10"></th></tr>
            </thead>
            <tbody>
              {(std.abbreviations || []).map((a, i) => (
                <tr key={i} className="border-t border-[#1E293B]">
                  <td className="px-2 py-1"><input value={a.short} onChange={(e)=>updateAbbr(i,'short',e.target.value)} placeholder="PPE" className="w-full bg-transparent px-2 py-1 text-sm text-slate-100 mono outline-none" data-testid={`abbr-short-${i}`}/></td>
                  <td className="px-2 py-1"><input value={a.long} onChange={(e)=>updateAbbr(i,'long',e.target.value)} placeholder="Personal Protective Equipment" className="w-full bg-transparent px-2 py-1 text-sm text-slate-100 outline-none" data-testid={`abbr-long-${i}`}/></td>
                  <td className="px-2"><button onClick={()=>removeAbbr(i)} className="text-slate-500 hover:text-red-400" data-testid={`abbr-remove-${i}`}><Trash2 size={14}/></button></td>
                </tr>
              ))}
              {(std.abbreviations || []).length === 0 && <tr><td colSpan={3} className="text-center py-6 text-sm text-slate-500">No abbreviations yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <button onClick={addAbbr} className="mt-3 text-sm text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1" data-testid="add-abbr"><Plus size={14}/> Add abbreviation</button>
      </Section>

      <Section title="Formatting rules">
        <textarea rows={4} value={std.formatting_rules || ""} onChange={(e)=>setStd({...std, formatting_rules:e.target.value})} placeholder="Numbered sections. Tables for procedural steps. Body text 11pt Calibri." className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg p-3 text-sm text-slate-100" data-testid="std-formatting"/>
      </Section>

      <Section title="Custom instructions">
        <textarea rows={5} value={std.custom_guidance || ""} onChange={(e)=>setStd({...std, custom_guidance:e.target.value})} placeholder="Use Australian English. Use direct instructional language. Use metric units. Do not introduce requirements unsupported by supplied information." className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg p-3 text-sm text-slate-100" data-testid="std-custom"/>
      </Section>
    </div>
  );
}

function Section({title, children}) {
  return <div className="fx-panel p-5"><div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">{title}</div>{children}</div>;
}
